import {
  signInWithCustomToken,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signOut,
  sendPasswordResetEmail,
  User as FirebaseUser,
  UserCredential,
  AuthError,
  GoogleAuthProvider,
  OAuthProvider
} from 'firebase/auth';
import { dynamicFirebaseService } from './dynamicFirebaseService';
import {
  FirebaseUserData,
  AuthResult,
  PasswordResetResult,
  FirebaseAuthError,
  FirebaseUserConverter
} from '../types/auth';

/**
 * Firebase Authentication Service
 * Handles all Firebase authentication operations for the frontend
 */
export class FirebaseAuthService {
  /**
   * Convert Firebase User to our FirebaseUserData interface
   */
  private convertFirebaseUser: FirebaseUserConverter = (user: FirebaseUser): FirebaseUserData => {
    // Determine provider ID from user's provider data
    const providerId = user.providerData.length > 0 
      ? user.providerData[0].providerId 
      : 'firebase';

    return {
      uid: user.uid,
      email: user.email,
      emailVerified: user.emailVerified,
      displayName: user.displayName,
      photoURL: user.photoURL,
      providerId,
      createdAt: user.metadata.creationTime || new Date().toISOString(),
      lastSignInAt: user.metadata.lastSignInTime || new Date().toISOString()
    };
  };

  /**
   * Convert Firebase AuthError to our FirebaseAuthError interface
   */
  private convertFirebaseError = (error: AuthError): FirebaseAuthError => {
    return {
      code: error.code,
      message: this.getErrorMessageKey(error.code)
    };
  };

  /**
   * Map a Firebase error code to a stable i18n key. The key (not localized
   * text) is propagated through the auth flow and translated at the display
   * boundary, so the same message can be shown in any supported language.
   */
  private getErrorMessageKey = (errorCode: string): string => {
    switch (errorCode) {
      case 'auth/user-not-found':
        return 'errors.firebase.userNotFound';
      case 'auth/wrong-password':
        return 'errors.firebase.wrongPassword';
      case 'auth/invalid-email':
        return 'errors.firebase.invalidEmail';
      case 'auth/user-disabled':
        return 'errors.firebase.userDisabled';
      case 'auth/too-many-requests':
        return 'errors.firebase.tooManyRequests';
      case 'auth/network-request-failed':
        return 'errors.firebase.networkRequestFailed';
      case 'auth/invalid-credential':
        return 'errors.firebase.invalidCredential';
      case 'auth/popup-blocked':
        return 'errors.firebase.popupBlocked';
      case 'auth/popup-closed-by-user':
        return 'errors.firebase.popupClosedByUser';
      case 'auth/cancelled-popup-request':
        return 'errors.firebase.cancelledPopupRequest';
      case 'auth/account-exists-with-different-credential':
        return 'errors.firebase.accountExistsWithDifferentCredential';
      // The next three are provider-agnostic - they fire for Apple as readily as for Google, so
      // their translations must not name a provider, or they send whoever reads them down the
      // wrong path. See errors.firebase.* in src/i18n/locales.
      case 'auth/operation-not-allowed':
        return 'errors.firebase.operationNotAllowed';
      case 'auth/operation-not-supported-in-this-environment':
        return 'errors.firebase.operationNotSupported';
      case 'auth/unauthorized-domain':
        return 'errors.firebase.unauthorizedDomain';
      case 'auth/web-storage-unsupported':
        return 'errors.firebase.webStorageUnsupported';
      default:
        return 'errors.firebase.default';
    }
  };

  /**
   * Sign in with email and password
   */
  async signIn(email: string, password: string): Promise<AuthResult> {
    try {
      const auth = dynamicFirebaseService.getAuth();
      const userCredential: UserCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );
      
      const userData = this.convertFirebaseUser(userCredential.user);
      
      return {
        success: true,
        user: userData
      };
    } catch (error) {
      const authError = error as AuthError;
      const firebaseError = this.convertFirebaseError(authError);
      
      return {
        success: false,
        error: firebaseError.message
      };
    }
  }

  /**
   * Sign in with Google using popup flow
   */
  async signInWithGoogle(): Promise<AuthResult> {
    try {
      const auth = dynamicFirebaseService.getAuth();
      const googleProvider = dynamicFirebaseService.getGoogleProvider();
      
      console.log('Initiating Google sign-in with popup flow');
      const userCredential: UserCredential = await signInWithPopup(auth, googleProvider);
      const userData = this.convertFirebaseUser(userCredential.user);
      
      // Extract Google tokens from credential using credentialFromResult
      const credential = GoogleAuthProvider.credentialFromResult(userCredential);
      const accessToken = credential?.accessToken;
      const idToken = credential?.idToken;
      
      console.log('Google sign-in successful', { 
        uid: userData.uid, 
        email: userData.email,
        providerId: userData.providerId,
        hasAccessToken: !!accessToken,
        hasIdToken: !!idToken
      });
      
      return {
        success: true,
        user: userData,
        accessToken: accessToken || undefined,
        idToken: idToken || undefined
      } as AuthResult & { accessToken?: string; idToken?: string };
    } catch (error) {
      const authError = error as AuthError;
      console.error('Google sign-in error:', authError.code, authError.message);
      
      // Handle popup blocked - fallback to redirect flow
      if (authError.code === 'auth/popup-blocked') {
        console.log('Popup blocked, attempting redirect flow');
        try {
          const auth = dynamicFirebaseService.getAuth();
          const googleProvider = dynamicFirebaseService.getGoogleProvider();
          await signInWithRedirect(auth, googleProvider);
          // The result will be handled by getGoogleRedirectResult
          console.log('Redirect flow initiated successfully');
          return {
            success: true
          };
        } catch (redirectError) {
          const redirectAuthError = redirectError as AuthError;
          console.error('Redirect flow failed:', redirectAuthError.code, redirectAuthError.message);
          const firebaseError = this.convertFirebaseError(redirectAuthError);
          
          return {
            success: false,
            error: firebaseError.message
          };
        }
      }
      
      // Handle user cancellation gracefully (don't show error)
      if (authError.code === 'auth/popup-closed-by-user' || 
          authError.code === 'auth/cancelled-popup-request') {
        console.log('Google sign-in cancelled by user');
        return {
          success: false,
          error: undefined // No error message for user cancellation
        };
      }
      
      const firebaseError = this.convertFirebaseError(authError);
      
      return {
        success: false,
        error: firebaseError.message
      };
    }
  }

  /**
   * Sign in with Apple using popup flow (Sign in with Apple via the Firebase SDK,
   * mirroring the Google flow so the backend always receives a Firebase ID token)
   */
  async signInWithApple(): Promise<AuthResult> {
    try {
      const auth = dynamicFirebaseService.getAuth();
      const appleProvider = dynamicFirebaseService.getAppleProvider();

      console.log('Initiating Apple sign-in with popup flow');
      const userCredential: UserCredential = await signInWithPopup(auth, appleProvider);
      const userData = this.convertFirebaseUser(userCredential.user);

      // Extract Apple tokens from credential using credentialFromResult
      const credential = OAuthProvider.credentialFromResult(userCredential);
      const accessToken = credential?.accessToken;
      const idToken = credential?.idToken;

      console.log('Apple sign-in successful', {
        uid: userData.uid,
        email: userData.email,
        providerId: userData.providerId,
        hasAccessToken: !!accessToken,
        hasIdToken: !!idToken
      });

      return {
        success: true,
        user: userData,
        accessToken: accessToken || undefined,
        idToken: idToken || undefined
      } as AuthResult & { accessToken?: string; idToken?: string };
    } catch (error) {
      const authError = error as AuthError;
      console.error('Apple sign-in error:', authError.code, authError.message);

      // Handle popup blocked - fallback to redirect flow
      if (authError.code === 'auth/popup-blocked') {
        console.log('Popup blocked, attempting redirect flow');
        try {
          const auth = dynamicFirebaseService.getAuth();
          const appleProvider = dynamicFirebaseService.getAppleProvider();
          await signInWithRedirect(auth, appleProvider);
          // The result will be handled by getOAuthRedirectResult
          console.log('Redirect flow initiated successfully');
          return {
            success: true
          };
        } catch (redirectError) {
          const redirectAuthError = redirectError as AuthError;
          console.error('Redirect flow failed:', redirectAuthError.code, redirectAuthError.message);
          const firebaseError = this.convertFirebaseError(redirectAuthError);

          return {
            success: false,
            error: firebaseError.message
          };
        }
      }

      // Handle user cancellation gracefully (don't show error)
      if (authError.code === 'auth/popup-closed-by-user' ||
          authError.code === 'auth/cancelled-popup-request') {
        console.log('Apple sign-in cancelled by user');
        return {
          success: false,
          error: undefined // No error message for user cancellation
        };
      }

      const firebaseError = this.convertFirebaseError(authError);

      return {
        success: false,
        error: firebaseError.message
      };
    }
  }

  /**
   * Sign in with a Firebase custom token minted by the identity server.
   * Used by the server-side Apple flow, where the identity server has already validated the
   * Apple ID token and resolved the Firebase user itself.
   */
  async signInWithCustomToken(customToken: string): Promise<AuthResult> {
    try {
      const auth = dynamicFirebaseService.getAuth();
      const userCredential: UserCredential = await signInWithCustomToken(auth, customToken);
      const userData = this.convertFirebaseUser(userCredential.user);

      console.log('Custom token sign-in successful', {
        uid: userData.uid,
        email: userData.email,
        providerId: userData.providerId
      });

      return {
        success: true,
        user: userData
      };
    } catch (error) {
      const authError = error as AuthError;
      console.error('Custom token sign-in error:', authError.code, authError.message);

      // Not every failure here is a Firebase AuthError - an SDK-not-initialized error arrives as a
      // plain Error with no code, and mapping that through the code table yields the generic
      // errors.firebase.default, hiding the actual cause from anyone reading the UI. Passing the
      // raw message through is safe with i18next: an unknown key renders as itself.
      return {
        success: false,
        error: authError.code
          ? this.convertFirebaseError(authError).message
          : authError.message || 'errors.firebase.default'
      };
    }
  }

  /**
   * Handle Google redirect result after redirect flow
   */
  async getGoogleRedirectResult(): Promise<AuthResult> {
    try {
      console.log('Checking for Google redirect result');
      const auth = dynamicFirebaseService.getAuth();
      const result = await getRedirectResult(auth);
      
      if (result && result.user) {
        const userData = this.convertFirebaseUser(result.user);

        // Extract provider tokens from credential - the redirect result may come from
        // either the Google or the Apple flow, so pick the matching provider class
        const credential = userData.providerId === 'apple.com'
          ? OAuthProvider.credentialFromResult(result)
          : GoogleAuthProvider.credentialFromResult(result);
        const accessToken = credential?.accessToken;
        const idToken = credential?.idToken;

        console.log('OAuth redirect result successful', {
          uid: userData.uid,
          email: userData.email,
          providerId: userData.providerId,
          hasAccessToken: !!accessToken,
          hasIdToken: !!idToken
        });
        
        return {
          success: true,
          user: userData,
          accessToken: accessToken || undefined,
          idToken: idToken || undefined
        } as AuthResult & { accessToken?: string; idToken?: string };
      }
      
      // No redirect result (user didn't complete flow or no redirect occurred)
      console.log('No Google redirect result found');
      return {
        success: false,
        error: undefined // No error for missing redirect result
      };
    } catch (error) {
      const authError = error as AuthError;
      console.error('Google redirect result error:', authError.code, authError.message);
      const firebaseError = this.convertFirebaseError(authError);
      
      return {
        success: false,
        error: firebaseError.message
      };
    }
  }

  /**
   * Sign out the current user
   */
  async signOut(): Promise<AuthResult> {
    try {
      const auth = dynamicFirebaseService.getAuth();
      await signOut(auth);
      
      return {
        success: true
      };
    } catch (error) {
      const authError = error as AuthError;
      const firebaseError = this.convertFirebaseError(authError);
      
      return {
        success: false,
        error: firebaseError.message
      };
    }
  }

  /**
   * Send password reset email
   */
  async resetPassword(email: string): Promise<PasswordResetResult> {
    try {
      const auth = dynamicFirebaseService.getAuth();
      await sendPasswordResetEmail(auth, email);
      
      return {
        success: true,
        message: 'passwordReset.result.sent'
      };
    } catch (error) {
      const authError = error as AuthError;
      const firebaseError = this.convertFirebaseError(authError);
      
      return {
        success: false,
        message: 'passwordReset.result.failed',
        error: firebaseError.message
      };
    }
  }

  /**
   * Get the current Firebase ID token
   */
  async getCurrentUserToken(): Promise<string | null> {
    try {
      const auth = dynamicFirebaseService.getAuth();
      const user = auth.currentUser;
      if (!user) {
        return null;
      }
      
      return await user.getIdToken();
    } catch (error) {
      console.error('Error getting user token:', error);
      return null;
    }
  }

  /**
   * Get the current user data
   */
  getCurrentUser(): FirebaseUserData | null {
    const auth = dynamicFirebaseService.getAuth();
    const user = auth.currentUser;
    if (!user) {
      return null;
    }
    
    return this.convertFirebaseUser(user);
  }

  /**
   * Check if user is currently authenticated
   */
  isAuthenticated(): boolean {
    const auth = dynamicFirebaseService.getAuth();
    return auth.currentUser !== null;
  }
}

// Export singleton instance
export const firebaseAuthService = new FirebaseAuthService();